import { createFileRoute } from "@tanstack/react-router";
import { Bullets, LegalPage, Note, Section, Value } from "@/components/legal-page";
import { LEGAL } from "@/lib/legal";

export const Route = createFileRoute("/community-guidelines")({
  head: () => ({
    meta: [
      { title: "Xamseen — Community Guidelines" },
      {
        name: "description",
        content:
          "How to report well, how to treat people, what is not allowed on Xamseen, and how moderation decisions and appeals work.",
      },
      { property: "og:title", content: "Xamseen — Community Guidelines" },
      { property: "og:description", content: "Report honestly, protect people's privacy, and argue in good faith." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://xamseen.com/community-guidelines" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://xamseen.com/community-guidelines" }],
  }),
  component: GuidelinesPage,
});

const TOC = [
  { id: "spirit", label: "The spirit of Xamseen" },
  { id: "report-well", label: "How to report well" },
  { id: "privacy", label: "Protect other people's privacy" },
  { id: "not-allowed", label: "What is not allowed" },
  { id: "officials", label: "Officials, organisations and verification" },
  { id: "moderation", label: "How moderation works" },
  { id: "appeals", label: "Appeals" },
  { id: "reporting", label: "Reporting content to us" },
];

function GuidelinesPage() {
  return (
    <LegalPage
      title="Community Guidelines"
      intro="Xamseen works when reports are honest, specific and fair to the people involved. These guidelines are how we keep it that way. They apply to reports, posts, answers, comments, Spaces, projects and profiles alike."
      toc={TOC}
    >
      <Section id="spirit" title="The spirit of Xamseen">
        <p>
          Xamseen is for documenting real problems in real places so they can actually be fixed. Assume the person on the
          other side of a report is a neighbour, not an opponent. Describe the problem, not the person.
        </p>
      </Section>

      <Section id="report-well" title="How to report well">
        <Bullets
          items={[
            <>
              <strong>Report what you saw.</strong> First-hand observation, described accurately. If you are unsure about
              a detail, say so rather than filling the gap.
            </>,
            <>
              <strong>Be specific about the place.</strong> A junction, a street, a stretch of path. Vague reports cannot
              be acted on.
            </>,
            <>
              <strong>Show, don't accuse.</strong> A photo of a flooded pavement is useful. A photo of a person you blame
              for it is not.
            </>,
            <>
              <strong>One issue per report.</strong> It keeps tracking and resolution meaningful.
            </>,
            <>
              <strong>Confirm honestly.</strong> Use "I see this too" only when you have genuinely seen the same issue.
              Confirmations are how the community signals real prevalence — inflating them breaks the whole mechanism.
            </>,
            <>
              <strong>Update when things change.</strong> If an issue is fixed, say so. Resolved reports are how Xamseen
              shows progress rather than just complaint.
            </>,
          ]}
        />
      </Section>

      <Section id="privacy" title="Protect other people's privacy">
        <p>Documenting a public problem does not require exposing private individuals.</p>
        <Bullets
          items={[
            <>Do not publish home addresses, phone numbers, identity documents, medical or financial information.</>,
            <>
              Avoid identifiable faces and licence plates where they are not the point of the report. Crop or blur them.
            </>,
            <>Do not post images of children who are identifiable, unless you are their parent or guardian.</>,
            <>Do not report the inside of someone's home or private property as if it were a public space.</>,
            <>Never publish someone's private information to punish, intimidate or expose them.</>,
          ]}
        />
      </Section>

      <Section id="not-allowed" title="What is not allowed">
        <Bullets
          items={[
            <>
              <strong>Harassment and hate.</strong> Targeted abuse, threats, or attacks based on ethnicity, nationality,
              religion, gender, sexuality, disability or similar characteristics.
            </>,
            <>
              <strong>Violence.</strong> Threats, incitement, glorification of violence, or content promoting violent
              organisations.
            </>,
            <>
              <strong>False or manipulated reports.</strong> Fabricated issues, staged photos, misrepresented locations,
              or coordinated vote and confirmation manipulation.
            </>,
            <>
              <strong>Doxxing.</strong> Publishing private information about a person without consent.
            </>,
            <>
              <strong>Sexual content.</strong> Pornographic material, and absolutely any sexual content involving minors —
              which we remove and report to the appropriate authorities.
            </>,
            <>
              <strong>Impersonation.</strong> Posing as another person, an organisation, an official or an authority.
            </>,
            <>
              <strong>Spam and scams.</strong> Bulk unsolicited posting, phishing, fraud, malware, or link schemes.
            </>,
            <>
              <strong>Illegal activity.</strong> Using Xamseen to commit, coordinate or facilitate unlawful acts.
            </>,
            <>
              <strong>Weaponising Xamseen.</strong> Using reports to surveil, stalk, target or build a case against a
              specific individual.
            </>,
          ]}
        />
      </Section>

      <Section id="officials" title="Officials, organisations and verification">
        <p>
          Government bodies, agencies, utilities and civic organisations can hold official accounts and respond to reports
          on the record. Official and verified labels are granted by Xamseen based on our checks, and may be removed if the
          basis for them changes.
        </p>
        <p>
          Official responses are held to the same guidelines as everyone else, and are expected to be accurate, on-topic and
          respectful. Official status does not make a claim true, and it does not exempt an account from moderation.
        </p>
      </Section>

      <Section id="moderation" title="How moderation works">
        <p>
          We review content that is reported to us, and we also act on patterns we detect ourselves. Depending on severity,
          history and intent, we may:
        </p>
        <Bullets
          items={[
            <>Leave the content up, if it does not breach these guidelines.</>,
            <>Add context, or reduce how widely the content is distributed.</>,
            <>Remove the content.</>,
            <>Limit specific features for an account, such as posting or confirming.</>,
            <>Suspend the account temporarily.</>,
            <>Terminate the account permanently, for severe or repeated breaches.</>,
          ]}
        />
        <p>
          Automated signals may flag content for review, but enforcement decisions with real consequences are reviewed by a
          person. Where it is reasonable and lawful to do so, we tell you what was actioned and why.
        </p>
        <Note tone="warn">
          Xamseen is not an emergency service. If someone is in danger, contact your local emergency number first — then, if
          it helps, document the underlying issue here.
        </Note>
      </Section>

      <Section id="appeals" title="Appeals">
        <p>
          If we remove your content or restrict your account and you believe we got it wrong, you can appeal. Tell us what
          was actioned and why you think the decision was mistaken. A person reviews the appeal, and if we were wrong we
          restore the content or the access and correct the enforcement record.
        </p>
        <p>
          Appeal by contacting <Value value={LEGAL.supportEmail} label="general support and legal contact address" />.
        </p>
      </Section>

      <Section id="reporting" title="Reporting content to us">
        <p>
          Use the report action on a post, comment or profile to flag something for review. Tell us what specifically is
          wrong — which guideline you think it breaches — because that makes review faster and more accurate. Do not use the
          report action to express disagreement with a report you think is inconvenient; that wastes review capacity that
          real harm needs.
        </p>
        <p>
          For urgent safety concerns, legal notices or intellectual property complaints, contact{" "}
          <Value value={LEGAL.supportEmail} label="general support and legal contact address" /> directly.
        </p>
      </Section>
    </LegalPage>
  );
}
